import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import BlockIcon from '@mui/icons-material/Block';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const STATUS_BANNER_CONFIG = {
  DONE: {
    icon: CheckCircleOutlineIcon,
    label: 'Submitted',
    description: 'This form has been submitted and is now read-only.',
    lightColor: '#15803d',
    lightBg: '#f0fdf4',
    lightBorder: '#dcfce7',
    darkColor: '#86efac',
    darkBg: 'rgba(21,128,61,0.12)',
    darkBorder: 'rgba(21,128,61,0.25)',
  },
  CANCELLED: {
    icon: CancelOutlinedIcon,
    label: 'Cancelled',
    description: 'This form has been cancelled and can no longer be submitted.',
    lightColor: '#71717a',
    lightBg: '#f4f4f5',
    lightBorder: '#e4e4e7',
    darkColor: '#a1a1aa',
    darkBg: 'rgba(113,113,122,0.12)',
    darkBorder: 'rgba(113,113,122,0.25)',
  },
  LOCKED: {
    icon: LockOutlinedIcon,
    label: 'Locked',
    description: 'This form is locked and cannot be edited.',
    lightColor: '#a16207',
    lightBg: '#fefce8',
    lightBorder: '#fef9c3',
    darkColor: '#fbbf24',
    darkBg: 'rgba(161,98,7,0.12)',
    darkBorder: 'rgba(161,98,7,0.25)',
  },
  VIEW_ONLY: {
    icon: BlockIcon,
    label: 'View only',
    description: 'This form is view-only and cannot be submitted.',
    lightColor: '#71717a',
    lightBg: '#f4f4f5',
    lightBorder: '#e4e4e7',
    darkColor: '#a1a1aa',
    darkBg: 'rgba(113,113,122,0.12)',
    darkBorder: 'rgba(113,113,122,0.25)',
  },
};

export function resolveFormStatusBannerKey(form) {
  if (!form) return null;
  if (form.progressStatus === 'DONE') return 'DONE';
  if (form.progressStatus === 'CANCELLED') return 'CANCELLED';
  if (form.locked) return 'LOCKED';
  if (form.responseMode === 'VIEW_ONLY') return 'VIEW_ONLY';
  return null;
}

export default function FormStatusBanner({ status }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const config = STATUS_BANNER_CONFIG[status];
  if (!config) return null;
  const Icon = config.icon;
  const color = isDark ? config.darkColor : config.lightColor;
  const bg = isDark ? config.darkBg : config.lightBg;
  const border = isDark ? config.darkBorder : config.lightBorder;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 2,
        borderRadius: '12px',
        bgcolor: bg,
        border: `1px solid ${border}`,
      }}
    >
      <Icon sx={{ fontSize: 22, color, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color, lineHeight: 1.3 }}>
          {config.label}
        </Typography>
        <Typography sx={{ fontSize: 12, color, opacity: 0.85, lineHeight: 1.4, mt: 0.25 }}>
          {config.description}
        </Typography>
      </Box>
    </Box>
  );
}
